import { AdvancedAnalyticsEntityType } from 'components/modal/modalList/graphDetails/components/AdvancedAnalyticsEntityTypes/AdvancedAnalyticsEntityTypes';
import { wlConfig } from 'config/wls/wlConfig';
import { Options } from 'highcharts';
import { buildQuickFilters } from 'helpers/quickFiltersBuilder';
import { QuickFilterConfig } from 'types/QuickFilters';

type WlId = number | null | undefined;
type WlConfigEntry = (typeof wlConfig)[keyof typeof wlConfig];

export const getWl = (wlId: WlId) => {
  if (!wlId) return null;
  return wlConfig[wlId as keyof typeof wlConfig] ?? null;
};

export const isWl = (wlId: WlId, target: number) => wlId === target;

export const getComponentVersion = (wlId: WlId): string => {
  const wl = getWl(wlId);
  return wl?.isNewComponentsEnabled && wl.componentsVersion
    ? wl.componentsVersion
    : 'default';
};

export const getMenuConfig = (wlId: WlId, fallbackMenu: any[]) => {
  const wl = getWl(wlId);
  return wl?.menuConfiguration ?? fallbackMenu;
};

export const getQuickFiltersConfig = (
  wlId: WlId,
  tableName: string,
  defaultConfig: QuickFilterConfig[]
) => {
  const wl = getWl(wlId);
  const overrides = wl?.quickFiltersOverride;

  return overrides
    ? buildQuickFilters(defaultConfig, overrides, tableName)
    : defaultConfig;
};

export const getTableConfig = (
  wlId: WlId,
  tableName: string,
  fallback?: any
) => {
  const wl = getWl(wlId);
  return wl?.tableConfig?.[tableName] ?? fallback;
};

/**
 * Returns resolved table props ready to spread into a table component.
 *
 * Both `rowRender` and `headerRender` are stored in wlConfig as factories
 * that receive a "render bag" — an object the page builds from its own props
 * and methods. This lets WL renderers access anything they need (callbacks,
 * translate, config, etc.) without the resolver needing to know about it.
 *
 *   headerRender: (bag) => ReactNode
 *   rowRender:    (bag) => (data, index) => ReactNode
 *
 * Props absent from the WL config are simply omitted, so the table falls back
 * to its own internal rendering logic.
 */
export const getResolvedTableProps = (
  wlId: WlId,
  tableName: string,
  bag: Record<string, any> = {}
): Record<string, any> => {
  const config = getTableConfig(wlId, tableName);
  if (!config?.props) return {};

  const { headerRender, rowRender, ...rest } = config.props;

  return {
    ...rest,
    ...(headerRender ? { headerRender: headerRender(bag) } : {}),
    ...(rowRender ? { rowRender: rowRender(bag) } : {}),
  };
};

export const resolveWlValue = <T>(
  wlId: WlId,
  selector: (config: WlConfigEntry) => T | undefined,
  fallback: T
): T => {
  const config = getWl(wlId);

  if (!config) {
    return fallback;
  }

  return selector(config) ?? fallback;
};

export const getWlChartSettingsFormConfig = (
  wlId: WlId,
  chartType: AdvancedAnalyticsEntityType,
  fallback?: any
) => {
  const wl = getWl(wlId);
  return wl?.chartSettingsFormConfig?.[chartType] ?? fallback;
};

export const getWlChartStyleTransformer = (wlId: WlId): Options | null => {
  const wl = getWl(wlId);
  return wl?.chartStyleTransformer ?? null;
};

export const getWlChartOptionsTransformer = (
  wlId: WlId
): ((options: Options, chartType?: string) => Options) | null => {
  const wl = getWl(wlId);
  return wl?.chartOptionsTransformer ?? null;
};

export const getHiddenProfileFields = (wlId: WlId): string[] => {
  const wl = getWl(wlId);
  return wl?.profileFieldsOverride
    ? Object.entries(wl.profileFieldsOverride)
        .filter(([_, value]) => value.hidden)
        .map(([key]) => key)
    : [];
};
